/**
 * The note, tags and rating on one trade.
 *
 * **Nothing here touches the numbers.** Entry, exit, P&L, R-multiple and MAE/MFE
 * are reconstructed from broker fills and are not editable — a journal a trader
 * can retouch is a journal that eventually agrees with how they remember the
 * trade rather than how it happened. What the user owns is the interpretation:
 * why they took it, how it felt, what to call it.
 *
 * Limits mirror `JournalEntrySchema` exactly (20,000 characters of notes,
 * rating 1–5). Enforced here so a long note fails at the keyboard rather than
 * after a round trip, and enforced there because a client check is a courtesy,
 * not a control.
 *
 * **Tags are no longer part of this form.** They moved to a vocabulary in
 * migration 017 and are written by their own endpoint, so `TagPicker` saves
 * them itself rather than waiting on this form's Save button. Two save paths on
 * one panel is a small cost; the alternative — free text on the entry — is the
 * synonym problem `strategy.ts` warns about.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import {
  api, ApiError, fmtNum, fmtTime, fmtUsd,
  type Trade,
} from '../lib/api.js';
import { Pnl } from './panels.js';
import { TradeAttachments } from './TradeAttachments.js';
import { TagPicker } from './TagPicker.js';

const MAX_NOTES = 20_000;

export function JournalEditor({
  trade, onClose,
}: {
  trade: Trade;
  onClose(): void;
}): JSX.Element {
  const [notes, setNotes] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [rating, setRating] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reload whenever the selected trade changes — the same editor instance is
  // reused as the user clicks down the table.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSaved(false);

    void api.journal(trade.id)
      .then((entry) => {
        if (cancelled) return;
        setNotes(entry.notes ?? '');
        setTagIds([...(entry.tagIds ?? [])]);
        setRating(entry.rating ?? null);
      })
      .catch((e) => {
        // 404 means "no entry yet", which is the common case, not a failure.
        if (cancelled) return;
        if (!(e instanceof ApiError) || e.status !== 404) {
          setError('Could not load this entry.');
        }
        setNotes(''); setRating(null);
        // Tags live in their own table, so a 404 on the note says nothing
        // about them — load them separately rather than clearing.
        void api.tradeTags()
          .then((m) => { if (!cancelled) setTagIds([...(m.trades[trade.id] ?? [])]); })
          .catch(() => { /* the picker shows its own error */ });
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [trade.id]);

  /**
   * Tags save on change, not on Save.
   *
   * A picker that silently discarded a tag because the user navigated away
   * without pressing a button elsewhere on the panel would be the kind of
   * quiet data loss that stops a trader tagging at all.
   */
  async function saveTags(next: string[]): Promise<void> {
    const previous = tagIds;
    setTagIds(next);
    try {
      await api.setTradeTags(trade.id, next);
    } catch {
      setTagIds(previous);
      setError('Could not save those tags.');
    }
  }

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await api.saveJournal(trade.id, { notes, rating, emotions: [] });
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <aside className="journal-editor" aria-label={`Journal entry for ${trade.symbol}`}>
      <div className="panel-head">
        <h3 className="panel-title">{trade.symbol} · {trade.direction}</h3>
        <button className="compact editor-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <dl className="kv kv-tight">
        <dt>Opened</dt><dd className="mono">{fmtTime(trade.openedAt)}</dd>
        <dt>Entry</dt><dd className="mono">{fmtNum(trade.avgEntryPrice)}</dd>
        <dt>Exit</dt>
        <dd className="mono">{trade.avgExitPrice ? fmtNum(trade.avgExitPrice) : 'open'}</dd>
        <dt>Net P&amp;L</dt><dd><Pnl value={trade.netPnl} /></dd>
        <dt>R</dt>
        <dd className="mono">{trade.rMultiple ? fmtNum(trade.rMultiple, 2) : '—'}</dd>
        <dt>MAE / MFE</dt>
        <dd className="mono dim">
          {trade.mae ? fmtUsd(trade.mae) : '—'} / {trade.mfe ? fmtUsd(trade.mfe) : '—'}
        </dd>
      </dl>

      <p className="field-hint">
        Reconstructed from broker fills and not editable. Only the notes below are
        yours to change.
      </p>

      {loading ? (
        <p className="dim">Loading…</p>
      ) : (
        <>
          <div className="field">
            <span className="field-label">Rating</span>
            <div className="rating" role="group" aria-label="Trade rating, 1 to 5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`rating-btn compact ${rating !== null && n <= rating ? 'on' : ''}`}
                  aria-pressed={rating !== null && n <= rating}
                  aria-label={`${n} out of 5`}
                  onClick={() => { setRating(rating === n ? null : n); setSaved(false); }}
                >
                  {/* A glyph and a pressed state, not colour alone. */}
                  {rating !== null && n <= rating ? '★' : '☆'}
                </button>
              ))}
              {rating !== null && (
                <span className="field-hint">{rating}/5 — click again to clear</span>
              )}
            </div>
          </div>

          <TagPicker value={tagIds} onChange={(next) => void saveTags(next)} />

          <label className="field">
            <span className="field-label">Notes</span>
            <textarea
              className="input textarea"
              rows={8}
              value={notes}
              maxLength={MAX_NOTES}
              onChange={(e) => { setNotes(e.target.value); setSaved(false); }}
              placeholder="What did you see? What would you do differently?"
            />
            <span className="field-hint">
              {notes.length.toLocaleString('en-US')} / {MAX_NOTES.toLocaleString('en-US')}
            </span>
          </label>

          {error && <div className="form-error" role="alert">{error}</div>}
          {saved && <div className="form-ok" role="status">Saved.</div>}

          <button className="btn btn-primary btn-block" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save entry'}
          </button>

          {/* Screenshots and the share link live below the save button rather
              than above it: they save themselves the moment they are used, and
              putting them in the middle of a form with an explicit save would
              imply they need one. */}
          <TradeAttachments tradeId={trade.id} />
        </>
      )}
    </aside>
  );
}
